import { Controller } from "@hotwired/stimulus";

/**
 * News feed — reactions and « I have read this » under a card. A click on
 * the button is « Like », a heart that turns green (or takes the current
 * reaction back); hovering it,
 * or the down arrow, opens the five reactions. The button changes at once
 * and goes back to what it was if the server refuses.
 */
export default class extends Controller {
  static targets = ["button", "icon", "label", "picker", "count", "ack"];
  static values = { url: String, ackUrl: String, mine: String, labels: Object, icons: Object, defaultLabel: String, failed: String };

  show() {
    clearTimeout(this.hideTimer);
    if (!this.buttonTarget.disabled) this.pickerTarget.hidden = false;
  }

  hideSoon() {
    this.hideTimer = setTimeout(() => { this.pickerTarget.hidden = true; }, 250);
  }

  toggle() {
    this.send(this.mineValue ? null : "like");
  }

  choose(event) {
    const kind = event.currentTarget.dataset.kind;
    this.pickerTarget.hidden = true;
    this.send(kind === this.mineValue ? null : kind);
  }

  async send(kind) {
    const before = this.mineValue;
    this.paint(kind);
    try {
      const response = await fetch(this.urlValue, {
        method: kind ? "PUT" : "DELETE",
        headers: this.headers,
        body: kind ? JSON.stringify({ kind }) : null,
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("refused");
      const state = await response.json();
      this.paint(state.mine);
      this.countTarget.textContent = state.count_label || "";
      this.countTarget.hidden = !state.count_label;
    } catch (_) {
      this.paint(before || null);
      window.g4sToast?.({ tone: "danger", message: this.failedValue });
    }
  }

  async acknowledge() {
    const button = this.ackTarget;
    const label = button.querySelector("span");
    const before = label.textContent;
    button.disabled = true;
    button.classList.add("is-done");
    label.textContent = button.dataset.doneLabel;
    try {
      const response = await fetch(this.ackUrlValue, { method: "POST", headers: this.headers, credentials: "same-origin" });
      if (!response.ok) throw new Error("refused");
    } catch (_) {
      button.disabled = false;
      button.classList.remove("is-done");
      label.textContent = before;
      window.g4sToast?.({ tone: "danger", message: this.failedValue });
    }
  }

  paint(kind) {
    this.mineValue = kind || "";
    this.buttonTarget.classList.toggle("is-active", Boolean(kind));
    this.buttonTarget.setAttribute("aria-pressed", String(Boolean(kind)));
    // « Like » is a heart alone, green once given; another reaction shows its name.
    this.buttonTarget.classList.toggle("is-heart", !kind || kind === "like");
    this.labelTarget.textContent = kind ? this.labelsValue[kind] : this.defaultLabelValue;
    this.buttonTarget.title = this.labelTarget.textContent;
    this.iconTarget.innerHTML = `<i data-lucide="${this.iconsValue[kind || "like"]}" class="ds-icon"></i>`;
    this.pickerTarget.querySelectorAll("[data-kind]").forEach((choice) => choice.classList.toggle("is-active", choice.dataset.kind === kind));
    window.g4sRenderIcons?.();
  }

  get headers() {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
    };
  }
}
